import React, { useState, useEffect, useCallback } from "react";
import { getSurvaySupport } from "../Services/contactSupportService";
import { surveySupportOptions } from "../Router/constant";
import { FiCalendar, FiChevronLeft, FiChevronRight, FiEye, FiSearch } from "react-icons/fi";
import { MdFindInPage, MdOutlineAssignmentInd } from "react-icons/md";
import Skeleton from "../Components/SkeletonLoader";
import ViewSurveyModal from "../Components/ViewSurveyModal";

const getSurveyRecords = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (!payload || typeof payload !== "object") {
    return [];
  }

  if (Array.isArray(payload.surveys)) {
    return payload.surveys;
  }

  if (Array.isArray(payload.contacts)) {
    return payload.contacts;
  }

  if (Array.isArray(payload.survey)) {
    return payload.survey;
  }

  if (Array.isArray(payload.records)) {
    return payload.records;
  }

  if (Array.isArray(payload.results)) {
    return payload.results;
  }

  if (Array.isArray(payload.docs)) {
    return payload.docs;
  }

  if (Array.isArray(payload.items)) {
    return payload.items;
  }

  if (Array.isArray(payload.rows)) {
    return payload.rows;
  }

  if (Array.isArray(payload.data)) {
    return payload.data;
  }

  if (payload.data && typeof payload.data === "object") {
    return getSurveyRecords(payload.data);
  }

  if (payload._id) {
    return [payload];
  }

  return [];
};

const getFirstNumber = (sources, keys) => {
  for (const source of sources) {
    if (!source || typeof source !== "object") {
      continue;
    }

    for (const key of keys) {
      if (source[key] !== undefined && source[key] !== null) {
        const numberValue = Number(source[key]);

        if (Number.isFinite(numberValue)) {
          return numberValue;
        }
      }
    }
  }

  return null;
};

const logisticsTypeLabels = {
  1: "Air freight",
  2: "Sea freight",
  3: "Rail freight",
  4: "Trucking",
  5: "Warehouse",
};

const getSurveyCount = (responseBody, fallback) => {
  const data = responseBody?.data;
  const sources = [
    data,
    data?.pagination,
    data?.meta,
    data?.pageInfo,
    responseBody,
    responseBody?.pagination,
    responseBody?.meta,
    responseBody?.pageInfo,
  ];

  return (
    getFirstNumber(sources, [
      "total",
      "count",
      "totalCount",
      "total_count",
      "totalRecords",
      "total_records",
      "totalItems",
      "total_items",
      "totalDocs",
      "total_docs",
      "totalSurvey",
      "totalSurveys",
    ]) ?? fallback
  );
};

const getSurveyTotalPages = (responseBody, count, limit) => {
  const data = responseBody?.data;
  const sources = [
    data,
    data?.pagination,
    data?.meta,
    data?.pageInfo,
    responseBody,
    responseBody?.pagination,
    responseBody?.meta,
    responseBody?.pageInfo,
  ];

  return (
    getFirstNumber(sources, [
      "totalPages",
      "total_pages",
      "totalPage",
      "total_page",
      "pages",
      "pageCount",
      "page_count",
    ]) ?? Math.ceil(count / limit)
  );
};

export default function Survey() {
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState("");
  const [logisticsType, setLogisticsType] = useState("");
  const [contactSupportData, setContactSupportData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  const fetchSupportData = useCallback(async () => {
    setLoading(true);

    try {
      const contactResp = await getSurvaySupport({
        page,
        limit,
        search,
        logisticsType,
      });
      const responseBody = contactResp.data;
      const responseData = responseBody?.data;
      const data = getSurveyRecords(responseData ?? responseBody);
      const totalRecords = getSurveyCount(responseBody, data.length);
      setContactSupportData(data);
      setTotalPages(getSurveyTotalPages(responseBody, totalRecords, limit));
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, logisticsType]);

  useEffect(() => {
    fetchSupportData();
  }, [fetchSupportData]);

  const handleView = (contact) => {
    setSelectedContact(contact);
    setOpenModal(true);
  };

  return (
    <div className="container-fluid qd-page">
      <div className="qd-card qd-panel">
        <div className="qd-panel-header">
          <div className="qd-panel-title">
            <span className="qd-panel-icon">
              <MdOutlineAssignmentInd />
            </span>
            <div>
              <h6>Survey contact details</h6>
              <p className="qd-panel-sub">View all survey contacts submitted by users.</p>
            </div>
          </div>
          <div className="qd-bookings-tools">
            <label className="qd-search">
              <FiSearch />
              <input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
              />
            </label>
            <select
              className="qd-select qd-select-wide qs-select"
              value={logisticsType}
              onChange={(e) => {
                setPage(1);
                setLogisticsType(e.target.value);
              }}
              aria-label="Filter by logistics type"
            >
              {surveySupportOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="qd-table-wrap">
          <table className="qd-table qs-table">
            <thead>
              <tr>
                <th>Company name</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone number</th>
                <th>Date</th>
                <th>Logistics type</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td><Skeleton width={120} /></td>
                    <td><Skeleton width={100} /></td>
                    <td><Skeleton width={150} /></td>
                    <td><Skeleton width={100} /></td>
                    <td><Skeleton width={90} /></td>
                    <td><Skeleton width={90} height={28} /></td>
                    <td><Skeleton width={80} height={32} /></td>
                  </tr>
                ))
              ) : contactSupportData.length === 0 ? (
                <tr>
                  <td colSpan={7} className="qu-empty-cell">
                    <div className="qu-empty">
                      <span className="qu-empty-art">
                        <MdFindInPage />
                      </span>
                      <h6>No survey contact details Found</h6>
                      <p>There are currently no survey contacts to display.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                contactSupportData.map((contact) => (
                  <tr key={contact._id}>
                    <td>
                      <span className="qd-customer">
                        <span className="qd-avatar">
                          {(contact.companyName || contact.fullName || "?").trim().charAt(0).toUpperCase()}
                        </span>
                        {contact.companyName || "--"}
                      </span>
                    </td>
                    <td>{contact.fullName || "--"}</td>
                    <td>{contact.email}</td>
                    <td>{contact.phoneNumber}</td>
                    <td>
                      <span className="qd-date">
                        <FiCalendar />
                        {new Date(contact.createdAt).toLocaleDateString("en-GB")}
                      </span>
                    </td>
                    <td>
                      {logisticsTypeLabels[contact?.logisticsType] ? (
                        <span className="qs-type">{logisticsTypeLabels[contact.logisticsType]}</span>
                      ) : (
                        "--"
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="qo-soft-btn qs-view-btn"
                        onClick={() => handleView(contact)}
                      >
                        <FiEye /> View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="qd-pagination">
            <span className="qd-muted">
              Page {page} of {totalPages}
            </span>
            <div className="qd-pages">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} aria-label="Previous">
                <FiChevronLeft />
              </button>
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index}
                  className={page === index + 1 ? "active" : ""}
                  onClick={() => setPage(index + 1)}
                >
                  {index + 1}
                </button>
              ))}
              <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} aria-label="Next">
                <FiChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>

      {openModal && (
        <ViewSurveyModal
          contact={selectedContact}
          onClose={() => setOpenModal(false)}
        />
      )}
    </div>
  );
}
